import { hubService, storeService, } from '@/services';
import { whereOperators } from '@/lib/query-parameter/filter';
import HubOperationSettingVM from '@/view-model/operation-setting/HubOperationSettingVM';
import StoreOperationSettingVM from '@/view-model/operation-setting/StoreOperationSettingVM';
import { targetType, } from '@/constants/operationSetting/option';
import { isApplyConfigSurchargeFormData, isDeliveryStatusFormData, isIrregularSurchargeFormData, } from '@/form/form-data/operationSettingSettingDialog';
import ApplyIrregularSurchargeFormDto from '@/form/form-dto/operation-setting/ApplyIrregularSurchargeFormDto';
import IrregularSurchargeFormDto from '@/form/form-dto/operation-setting/IrregularSurchargeFormDto';
import DeliveryStatusFormDto from '@/form/form-dto/operation-setting/DeliveryStatusFormDto';
import { vuexEventPathGenerator } from '@/utils';
import { mutationType } from './mutations';
export const actionType = {
    setIsOpenDialog: 'setIsOpenDialog',
    selectedTarget: 'selectedTarget',
    postOperationSetting: 'postOperationSetting',
    getHubInfo: 'getHubInfo',
    getHubList: 'getHubList',
    getStoreList: 'getStoreList',
    setSelectedHub: 'setSelectedHub',
    updateSearchQuery: 'updateSearchQuery',
    updateCheckBox: 'updateCheckBox',
    checkItem: 'checkItem',
    uncheckItem: 'uncheckItem',
    uncheckAllItem: 'uncheckAllItem',
    clearAllInput: 'clearAllInput',
};
export const operationSettingActionType = vuexEventPathGenerator(actionType, 'operationSetting');
const action = {
    [actionType.setIsOpenDialog]({ commit }, payload) {
        commit(mutationType.SET_IS_OPEN_DIALOG, payload);
    },
    [actionType.selectedTarget]({ commit }, target) {
        commit(mutationType.SELECTED_TARGET, target);
    },
    [actionType.clearAllInput]({ commit }) {
        commit(mutationType.CLEAR_ALL_INPUT);
    },
    [actionType.updateCheckBox]({ commit }, { target, selected }) {
        commit(mutationType.UPDATE_CHECK_BOX, {
            target,
            selected,
        });
    },
    [actionType.checkItem]({ commit }, { target, item }) {
        commit(mutationType.CHECK_ITEM, {
            target,
            item,
        });
    },
    [actionType.uncheckItem]({ commit }, { target, itemCode }) {
        commit(mutationType.UNCHECK_ITEM, {
            target,
            itemCode,
        });
    },
    [actionType.uncheckAllItem]({ commit }, { target }) {
        commit(mutationType.UNCHECK_ALL_ITEM, {
            target,
        });
    },
    async [actionType.postOperationSetting]({ state: { selectedTarget, }, commit, getters, }, { selectedType, formData, }) {
        // hub 운행설정인지 store 운행설정인지
        const targetService = selectedTarget === targetType.HUB
            ? hubService
            : storeService;
        // 그에 따른 체크된 아이템 선정
        const itemIds = selectedTarget === targetType.HUB
            ? getters.getterHubCheckedIdList
            : getters.getterStoreCheckedIdList;
        let formDto;
        if (isApplyConfigSurchargeFormData(formData, selectedType)) {
            const { isApply, surchargeReason, } = formData;
            formDto = new ApplyIrregularSurchargeFormDto({
                dialogType: selectedTarget,
                itemIds,
                isApply,
                surchargeReason,
            });
        }
        if (isDeliveryStatusFormData(formData, selectedType)) {
            const { deliveryStatusType, delayTime, delayReason, isApplyAllOrders, } = formData;
            formDto = new DeliveryStatusFormDto({
                dialogType: selectedTarget,
                itemIds,
                deliveryStatusType,
                delayTime,
                delayReason,
                isApplyAllOrders,
            });
        }
        if (isIrregularSurchargeFormData(formData, selectedType)) {
            const { isApply, charge, chargeRate, surchargeReason, } = formData;
            formDto = new IrregularSurchargeFormDto({
                dialogType: selectedTarget,
                itemIds,
                isApply,
                charge,
                chargeRate,
                surchargeReason,
            });
        }
        if (!formDto) {
            return false;
        }
        const res = await targetService.postOperationSetting(formDto);
        if (!res) {
            throw new Error('error');
        }
        commit(mutationType.POST_OPERATION_SETTING, {
            newData: formDto.updateViewModelDto(),
            target: selectedTarget,
        });
        return res;
    },
    /**
     * 현재 로그인한 사용자의 허브정보(허브 사용자 only)
     */
    [actionType.getHubInfo]({ commit, rootGetters }) {
        const defaultHubInfo = new HubOperationSettingVM(rootGetters['user/hubInfo']);
        commit(mutationType.SET_SELECTED_HUB, defaultHubInfo);
        commit(mutationType.GET_HUB_LIST, [
            defaultHubInfo,
        ]);
    },
    /**
     * 허브 선택은 선택한 허브를 강조하고, 선택한 허브를 노출하며, 상점 목록을 조회합니다.
     */
    [actionType.setSelectedHub]({ commit }, hub) {
        commit(mutationType.SET_SELECTED_HUB, hub);
    },
    /**
     * 허브 목록
     */
    async [actionType.getHubList]({ commit, rootGetters }) {
        try {
            const { list } = await hubService.getHubs({
                pageNumber: 1,
                pageSize: 10_000,
                useYn: 'Y',
            });
            const userHubId = rootGetters['user/hubId'];
            let defaultSelectedHubInfo = {};
            const hubList = list.reduce((acc, item) => {
                const defaultHubInfo = new HubOperationSettingVM(item);
                if (defaultHubInfo.code === userHubId) {
                    acc.unshift(defaultHubInfo);
                    defaultSelectedHubInfo = defaultHubInfo;
                    return acc;
                }
                return acc.concat(defaultHubInfo);
            }, []);
            commit(mutationType.SET_SELECTED_HUB, defaultSelectedHubInfo);
            commit(mutationType.GET_HUB_LIST, hubList);
        }
        catch (error) {
            console.log('[getHubs]', error);
            throw error;
        }
    },
    /**
     * 상점 목록
     */
    async [actionType.getStoreList]({ commit, getters }) {
        commit(mutationType.GET_STORE_LIST, []);
        try {
            const hubId = getters.getterSelectedHubId;
            const filters = [];
            // 정상 상태인 것만 조회한다.
            // https://barogo.slack.com/archives/C01A1R67RFA/p1739263358202749?thread_ts=1739239988.655179&cid=C01A1R67RFA
            filters.push({
                condition: whereOperators.equals,
                field: 'useYn',
                value: 'ON',
            });
            filters.push({
                condition: whereOperators.equals,
                field: 'hubId',
                value: hubId,
            });
            const { list } = await storeService.getStoreList({
                filters,
            });
            commit(mutationType.GET_STORE_LIST, list.map((_store) => new StoreOperationSettingVM(_store)));
        }
        catch (error) {
            console.log('[getStoreList]', error);
            throw error;
        }
    },
    /**
     * 검색
     */
    async [actionType.updateSearchQuery]({ commit }, { target, word }) {
        commit(mutationType.UPDATE_SEARCH_QUERY, {
            target,
            word,
        });
    },
};
export default action;
